/**
 * Centralised keyboard-shortcut dispatcher. Pages register handlers
 * with `bind()`; the registry is cleared on route change.
 *
 * Shortcuts skip typing fields. `?` opens the help overlay globally.
 */
type Handler = (e: KeyboardEvent) => void;

const table = new Map<string, Handler>();

export function bind(key: string, fn: Handler): () => void {
  table.set(key, fn);
  return () => table.delete(key);
}

export function clearAll(): void {
  table.clear();
}

export function dispatch(e: KeyboardEvent): boolean {
  if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
    return false;
  }
  const key = normalize(e);
  const h = table.get(key);
  if (!h) return false;
  h(e);
  return true;
}

function normalize(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.ctrlKey)  parts.push("Ctrl");
  if (e.shiftKey) parts.push("Shift");
  if (e.altKey)   parts.push("Alt");
  parts.push(e.key);
  return parts.join("+");
}

// Auto-install one window listener — per-key handlers live in `table`.
if (typeof window !== "undefined" && !(window as any).__vpShortcutsInstalled) {
  (window as any).__vpShortcutsInstalled = true;
  window.addEventListener("keydown", (e) => dispatch(e));
}
